import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, AlertTriangle, CalendarClock, UserCheck, FileText, MessagesSquare } from 'lucide-react';
import api from '../../../services/api';
import { offerTimelineItems } from './offerEvents';

const Wrapper = styled.div`
  padding: 8px 0 32px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 22px 24px;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 22px;
  &:before {
    content: '';
    position: absolute;
    left: 6px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: #E8ECF0;
  }
`;

const Entry = styled.div`
  position: relative;
  padding: 12px 0;
  &:before {
    content: '';
    position: absolute;
    left: -22px;
    top: 18px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme, $breach, $interview, $danger, $reference, $offer }) => ($breach || $danger) ? theme.colors.auxiliar.danger : $interview ? theme.colors.secondary.main : $reference ? theme.colors.auxiliar.warning : $offer ? theme.colors.text.primary : theme.colors.primary.main};
    border: 3px solid #fff;
    box-shadow: 0 0 0 1px ${({ theme, $breach, $interview, $danger, $reference, $offer }) => ($breach || $danger) ? theme.colors.auxiliar.danger : $interview ? theme.colors.secondary.main : $reference ? theme.colors.auxiliar.warning : $offer ? theme.colors.text.primary : theme.colors.primary.main};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StageBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  background: #EEF1F4;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 600;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Note = styled.div`
  margin-top: 6px;
  padding: 8px 10px;
  background: ${({ $danger }) => ($danger ? '#FDECEA' : '#FAFBFC')};
  border-left: 3px solid ${({ theme, $danger }) => ($danger ? theme.colors.auxiliar.danger : theme.colors.primary.main)};
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const DangerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  background: #FDECEA;
  color: #C62828;
  font-size: 12px;
  font-weight: 700;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const fmt = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleString(lang || 'tr'); } catch { return null; }
};

// FE-30 — BİRİM ETİKETLERİ SABİT KODLUYDU: `lang === 'tr' ? 'saat' : 'h'`.
// Dünya iki dilden ibaret sayılmıştı: Rusça, Kazakça ve Azerice kullanan İK
// uzmanı aşama süresini "3.5 h" diye görüyordu — ne kendi dili, ne de tam
// sözcük. Etiketler artık sözlükten gelir (beş locale'de de karşılığı var).
//
// İKİNCİ KUSUR (aynı satırda, envanterde YOK): `toFixed(1)` ondalık ayracı da
// sabitler — DAİMA nokta üretir. Türkçe/Rusça/Kazakça/Azerice'de ayraç
// VİRGÜLDÜR; sayfanın geri kalanı (tarih, tutar) zaten `Intl` ile
// yerelleştiriliyordu, yalnız bu satır "3.5" yazıyordu. `Intl.NumberFormat`
// hem ayracı hem binlik gruplamayı dile bırakır.
const fmtDecimal = (n, lang) => {
  try {
    return new Intl.NumberFormat(lang || 'tr', {
      minimumFractionDigits: 1, maximumFractionDigits: 1,
    }).format(n);
  } catch { return n.toFixed(1); }
};

const fmtHours = (h, lang, t) => {
  if (h == null) return null;
  if (h < 24) return t('ats.application.history.unit.hours', { value: fmtDecimal(h, lang) });
  return t('ats.application.history.unit.days', { value: fmtDecimal(h / 24, lang) });
};

export default function ApplicationHistoryTab({ applicationId }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Unified timeline: stage moves, interviews, reference checks AND offer
    // events. Each used to live on its own screen — an accepted offer never
    // showed up here (customer feedback 2026-08: the decisive milestone was
    // missing from "the candidate's full journey"). Merge all four sources
    // chronologically. Offers degrade gracefully: viewers outside the offer
    // scope (e.g. interview panelists) get an empty list via catch — offer
    // knowledge stays in the HM/recruiter domain, and amounts are never shown.
    Promise.all([
      api.get(`/v1/ats-applications/${applicationId}/stage-history`)
        .then((r) => (Array.isArray(r.data?.data) ? r.data.data : []))
        .catch(() => []),
      api.get(`/v1/ats-interviews?applicationId[eq]=${applicationId}&pageSize=100`)
        .then((r) => { const d = r.data?.data; return Array.isArray(d) ? d : (d?.data || []); })
        .catch(() => []),
      api.get(`/v1/ats-reference-checks/for-application/${applicationId}`)
        .then((r) => { const d = r.data?.data; return Array.isArray(d) ? d : (d?.data || []); })
        .catch(() => []),
      api.get(`/v1/ats-offers/for-application/${applicationId}`)
        .then((r) => { const d = r.data?.data; return Array.isArray(d) ? d : (d?.data || []); })
        .catch(() => []),
      // Panel debrief kayıtları (SPEC F4) — "Debrief: {karar}" olayı. Kapsam
      // dışı aktör boş liste alır; zaman çizelgesinin geri kalanı etkilenmez.
      api.get(`/v1/ats-applications/${applicationId}/debriefs`, { skipErrorToast: true })
        .then((r) => { const d = r.data?.data; return Array.isArray(d) ? d : (d?.data || []); })
        .catch(() => []),
    ]).then(([history, interviews, references, offers, debriefs]) => {
      if (cancelled) return;
      const ms = (x) => { const v = new Date(x).getTime(); return Number.isFinite(v) ? v : 0; };
      const merged = [
        ...history.map((h) => ({ kind: 'stage', ts: h.movedAt, data: h })),
        ...interviews.map((iv) => ({ kind: 'interview', ts: iv.scheduledStart, data: iv })),
        // A reference check is anchored at when it was added to the process
        // (createDate); the actual check date is surfaced as meta when present.
        ...references.map((rc) => ({ kind: 'reference', ts: rc.createDate, data: rc })),
        ...offerTimelineItems(offers),
        ...debriefs.map((db) => ({ kind: 'debrief', ts: db.heldAt || db.createDate, data: db })),
      ].sort((a, b) => ms(a.ts) - ms(b.ts));
      setItems(merged);
    });
    return () => { cancelled = true; };
  }, [applicationId]);

  if (items == null) return <Empty>{t('common.loading', { defaultValue: 'Yükleniyor...' })}</Empty>;

  return (
    <Wrapper>
      <Card>
        {items.length === 0 ? (
          <Empty>{t('ats.application.history.empty')}</Empty>
        ) : (
          <Timeline>
            {items.map((item) => item.kind === 'stage' ? (() => {
              const d = item.data;
              const isDisqualified = d.toStageType === 'disqualified';
              // System-placeholder notes carry no user info beyond the move itself.
              const note = d.reasonNote === 'applied'
                ? t('ats.application.history.note.applied')
                : d.reasonNote === 'disqualified'
                  ? null
                  : d.reasonNote;
              return (
                <Entry key={`s-${d.id}`} $breach={d.slaBreached} $danger={isDisqualified}>
                  <HeaderRow>
                    {d.fromStageName && (
                      <>
                        <StageBadge>{d.fromStageName}</StageBadge>
                        <ArrowRight size={14} />
                      </>
                    )}
                    <StageBadge>{d.toStageName}</StageBadge>
                    {isDisqualified && (
                      <DangerBadge>
                        <AlertTriangle size={12} />
                        {t('ats.application.history.disqualified')}
                      </DangerBadge>
                    )}
                  </HeaderRow>
                  <MetaRow>
                    <MetaItem><Clock size={12} />{fmt(d.movedAt, i18n.language)}</MetaItem>
                    {d.timeInPreviousStageHours != null && (
                      <MetaItem>{fmtHours(d.timeInPreviousStageHours, i18n.language, t)}</MetaItem>
                    )}
                    {d.slaBreached && (
                      <MetaItem style={{ color: '#E53935' }}>
                        <AlertTriangle size={12} />
                        {t('ats.application.history.slaBreached')}
                      </MetaItem>
                    )}
                  </MetaRow>
                  {note && (
                    <Note $danger={isDisqualified}>
                      {isDisqualified ? `${t('ats.application.history.disqualifyReason')}: ${note}` : note}
                    </Note>
                  )}
                </Entry>
              );
            })() : item.kind === 'interview' ? (
              <Entry key={`i-${item.data.id}`} $interview>
                <HeaderRow>
                  <CalendarClock size={14} />
                  <StageBadge>{t('ats.application.history.interview')}</StageBadge>
                  <span style={{ fontSize: 13 }}>
                    {t(`ats.interview.type.${item.data.interviewType}`, { defaultValue: item.data.interviewType })}
                  </span>
                </HeaderRow>
                <MetaRow>
                  <MetaItem><Clock size={12} />{fmt(item.data.scheduledStart, i18n.language)}</MetaItem>
                  <MetaItem>
                    {t(`ats.interview.status.${item.data.status}`, { defaultValue: item.data.status })}
                  </MetaItem>
                </MetaRow>
              </Entry>
            ) : item.kind === 'debrief' ? (
              <Entry key={`d-${item.data.id || item.data.interviewId}`} $interview>
                <HeaderRow>
                  <MessagesSquare size={14} />
                  <StageBadge data-role="ats-history-debrief">
                    {t('ats.debrief.history.title', {
                      decision: t(`ats.debrief.decision.${item.data.decision}`, { defaultValue: item.data.decision }),
                    })}
                  </StageBadge>
                </HeaderRow>
                <MetaRow>
                  <MetaItem><Clock size={12} />{fmt(item.ts, i18n.language)}</MetaItem>
                  {item.data.facilitatorName && (
                    <MetaItem>{t('ats.debrief.form.facilitator')}: {item.data.facilitatorName}</MetaItem>
                  )}
                </MetaRow>
                {item.data.summary && <Note>{item.data.summary}</Note>}
              </Entry>
            ) : item.kind === 'reference' ? (
              <Entry key={`r-${item.data.id}`} $reference>
                <HeaderRow>
                  <UserCheck size={14} />
                  <StageBadge>{t('ats.application.history.reference')}</StageBadge>
                  <span style={{ fontSize: 13 }}>{item.data.refereeName}</span>
                </HeaderRow>
                <MetaRow>
                  <MetaItem><Clock size={12} />{fmt(item.data.createDate, i18n.language)}</MetaItem>
                  <MetaItem>
                    {t(`ats.referenceCheck.status.${item.data.status}`, { defaultValue: item.data.status })}
                  </MetaItem>
                  {item.data.checkedAt && (
                    <MetaItem>{t('ats.referenceCheck.field.checkedAt')}: {fmt(item.data.checkedAt, i18n.language)}</MetaItem>
                  )}
                  {item.data.rating != null && (
                    <MetaItem>{t('ats.referenceCheck.field.rating')}: {item.data.rating}</MetaItem>
                  )}
                  {item.data.checkedByUserName && (
                    <MetaItem>{item.data.checkedByUserName}</MetaItem>
                  )}
                </MetaRow>
              </Entry>
            ) : (
              // Teklif olayı (müşteri geri bildirimi 2026-08): gönderim/kabul/ret,
              // versiyon rozetiyle. Tutar bilinçli olarak GÖSTERİLMEZ; withdrawn/
              // expired akıbeti gönderim olayının durum metasında görünür.
              <Entry key={`o-${item.data.id}-${item.data.event}`} $offer>
                <HeaderRow>
                  <FileText size={14} />
                  <StageBadge>
                    {t(`ats.application.history.offer.${item.data.event}`)}
                    {item.data.version > 1 ? ` (v${item.data.version})` : ''}
                  </StageBadge>
                </HeaderRow>
                <MetaRow>
                  <MetaItem><Clock size={12} />{fmt(item.ts, i18n.language)}</MetaItem>
                  {item.data.event === 'sent' && item.data.status !== 'sent' && (
                    <MetaItem>
                      {t(`ats.offer.status.${item.data.status}`, { defaultValue: item.data.status })}
                    </MetaItem>
                  )}
                </MetaRow>
                {item.data.reason && (
                  <Note>{t('ats.application.history.offer.rejectReason')}: {item.data.reason}</Note>
                )}
              </Entry>
            ))}
          </Timeline>
        )}
      </Card>
    </Wrapper>
  );
}
